var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.exploit.reserve",
  title: "EGT Exploit Reserve",
  description: "Dedicated /exploit/preorder page — the Named Edition preorder wizard, rendered INLINE (mirrors egt.reserve.js's pattern for the platform beta).",
  supportServer: true,
  fields: {}
});

var S = require("../helpers/egt.sections.js");

/* The dedicated /exploit/preorder page. The Named Edition wizard (email ->
 * verify -> shipping/consent -> Stripe payment) lives HERE, rendered INLINE —
 * not in a same-page dialog. exploit.reserve.js mounts the exact same proven
 * wizard (getWizardDoc + the same step handlers used by the old dialog flow)
 * into the on-page host [data-exploit-reserve-inline] below
 * (mountInlineReserveWizardAsync). Every preorder CTA on /exploit now links
 * here directly instead of opening a dialog over the book page.
 *
 * Copy leans on "preorder" over "reserve" throughout (founder call) — it's a
 * paid preorder of a physical book, not a reservation hold. */
/* Q2 (founder, 2026-07-22): "The 2 'hero' like sections are a waste of space and
 * may hinder actions." This surface used to open with renderHero() — a full
 * viewport `egt-canvas--tall` brand statement — followed by renderReassure(), a
 * second section with its own eyebrow/title/lead and a three-card grid. Two full
 * screens of argument before the buyer could reach the thing that takes money.
 * F1/F2 (founder, 2026-07-23): "fullscreen, centred form." The intermediate
 * compact lead (renderLead) is now gone too — the wizard IS the hero. renderFlow
 * is the page's opening screen: the frosted preorder card centred on the moving
 * grid canvas (egt-canvas--center --tall), over UNDIMMED media (agents.md §4: no
 * scrim). The reassurance substance lives in the wizard's step 1, read
 * immediately before starting rather than scrolled past. Do not re-add a hero or
 * a lead above the form. */
exports.render = async function render() {
  return S.page("/exploit/preorder", [
    preorderSchema(),
    renderFlow(),
    renderTerms()
  ], { navCta: { label: "Pre-order Exploit", href: "/exploit/preorder" } });
};

/* Per-surface JSON-LD: WebPage + BreadcrumbList + Book/Product for the
 * /exploit/preorder page. Same Book entity @id as /exploit (referenced across
 * both surfaces). Every fact is visibly on this page: title, byline, the
 * first-edition hardback + certificate format, and the flat $75 price
 * (preorder and post-launch are the SAME price — there is no ladder).
 * Offer is PreOrder, url = this page. NO isbn / no dates. Site-wide
 * Organization + WebSite come from S.page; URLs from S.siteOrigin(). */
function preorderSchema() {
  var O = S.siteOrigin();
  return S.jsonLd([
    { "@type": "WebPage", "@id": O + "/exploit/preorder#webpage",
      url: O + "/exploit/preorder",
      name: "Preorder Exploit — the Named Edition",
      isPartOf: { "@id": O + "/#website" },
      about: { "@id": O + "/exploit#book" },
      breadcrumb: { "@id": O + "/exploit/preorder#breadcrumb" } },
    { "@type": "BreadcrumbList", "@id": O + "/exploit/preorder#breadcrumb",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: O + "/" },
        { "@type": "ListItem", position: 2, name: "Exploit: Book One of The Universe", item: O + "/exploit" },
        { "@type": "ListItem", position: 3, name: "Preorder" }
      ] },
    { "@type": ["Book", "Product"], "@id": O + "/exploit#book", name: "Exploit: Book One of The Universe",
      /* Same @id entity as /exploit — keep alternateName aligned so the book
       * disambiguates from the hacking "exploit" collision on this surface too. */
      alternateName: ["Exploit", "Exploit (novel)", "Exploit: Book One of The Universe"],
      author: { "@type": "Person", name: "Justin K Kazmierczak" },
      inLanguage: "en", bookFormat: "https://schema.org/Hardcover",
      description: "Book One of The Universe — a cinematic spiritual thriller by Justin K Kazmierczak, offered as a first-edition hardback with a certificate of authenticity.",
      image: O + "/imgs/exploit/exploit-title-card.avif",
      brand: { "@id": O + "/#organization" },
      offers: { "@type": "Offer", url: O + "/exploit/preorder",
        price: "75", priceCurrency: "USD",
        availability: "https://schema.org/PreOrder",
        itemCondition: "https://schema.org/NewCondition",
        /* Shipping is a SEPARATE $7.99 charged once per order, never folded into
         * `price` — Google requires `price` to match the visible price, and the
         * visible price is $75. Keep this block BYTE-IDENTICAL to the one in
         * egt.exploit.js: both emit @id /exploit#book and a divergence
         * is a self-contradicting graph. */
        shippingDetails: { "@type": "OfferShippingDetails",
          shippingRate: { "@type": "MonetaryAmount", value: "7.99", currency: "USD" },
          shippingDestination: { "@type": "DefinedRegion", addressCountry: "US" } } } }
  ]);
}

/* THE FLOW — the inline Named Edition wizard, mounted by exploit.reserve.js
 * into [data-exploit-reserve-inline]. The section id is the scroll target
 * for the hero + close CTAs. */
function renderFlow() {
  return {
    n: "section", c: "egt-section egt-canvas egt-canvas--center egt-canvas--tall egt-reserve-flow", id: "preorder-wizard",
    i: [
      S.media({ img: "/imgs/exploit/exploit-hero-bg.jpg", alt: "", parallax: 0.08, priority: true }),
      { n: "div", c: "egt__shell egt-canvas__inner",
      i: { n: "div", c: "egt-glass egt-glass--wide egt-glass--dark egt-reserve-flow__card egt-reveal",
        i: [
          { n: "h1", c: "egt-reserve-flow__title", i: S.priceToken("Pre-order your physical hard copy — $75 + shipping.") },
          /* F3: the descriptive intro ("About a minute…") and the preorder-terms
           * jump link used to sit HERE, above the mount, so they showed on EVERY
           * wizard step. They now live INSIDE the wizard where they belong: the
           * intro is the first node of step 1 (shown once, then gone as the buyer
           * advances — see exploit.reserve.js step 1), and the full terms are the
           * dedicated Terms step (step 7) with an enforced "I agree". The VERBATIM
           * statutory delay/refund sentence still appears BEFORE the charge in the
           * review/terms step and in renderTerms below (16 CFR 435.2(a)(1)(i);
           * Cal. Bus. & Prof. Code § 17538) — do not delete those. The no-JS
           * fallback terms section (renderTerms) stays in the DOM for
           * JavaScript-disabled visitors; the wizard hides it on mount
           * (hideNoJsTermsFallback), which also no-ops harmlessly now that the
           * .egt-reserve-flow__lead--terms jump link no longer exists. */
          /* The Named framing used to sit HERE, above the whole wizard, where it
           * read as damage control for a promise nobody had been made. It now
           * lives inside the step that actually asks for the name — see
           * app/public/src/exploit.reserve.js ("A few things before you
           * checkout,"). Do not re-add it above the mount. */
          /* MOUNT POINT — exploit.reserve.js renders the proven ua.wizard here
           * (email -> verify -> shipping/consent -> Stripe). Empty until JS
           * mounts it; a no-JS visitor still gets the full disclosure via the
           * server-rendered terms section below (renderTerms). */
          { n: "div", c: "egt-reserve-flow__host", "data-exploit-reserve-inline": "true",
            i: { n: "p", c: "egt-reserve-flow__loading", role: "status", i: "Loading your preorder…" } }
        ]
      } }
    ]
  };
}

/* THE PREORDER TERMS — server-rendered, on the page itself, before any payment
 * can happen. Not a link, not a modal, not a checkbox: a visitor with JavaScript
 * disabled (so the wizard never mounts) still gets the whole disclosure.
 *
 * NO-JS FALLBACK NOW (F4/F12). For a JS buyer the terms are a WIZARD STEP — the
 * Terms step (step 7), with an explicit "I agree" that is enforced server-side
 * on the PaymentIntent mint (termsnotaccepted). Terms in two places would be the
 * duplication F4/F12 removed, so exploit.reserve.js HIDES this section (and the
 * flow header's jump link to it) the moment the wizard mounts
 * (hideNoJsTermsFallback). This stays in the DOM purely so a no-JS visitor keeps
 * the whole disclosure. The load-bearing verbatim pieces below (the statutory
 * italic and the § 17538 seller block) are BYTE-IDENTICAL to the Terms step so
 * the two can never diverge.
 *
 * Placement: BELOW the wizard mount, deliberately. The founder's Q2 ruling was
 * that argument above the form pushes the thing that takes money off the screen
 * — so the terms sit under it, reachable by the jump link in renderFlow, and the
 * load-bearing pieces are ALSO shown as the Terms step inside the wizard
 * (app/public/src/exploit.reserve.js). The buyer therefore cannot reach payment
 * without passing the statutory notice.
 *
 * Source and reasoning: task-tracker/research/commerce-preorder-terms.md §3.
 * Three passages here are LEGALLY LOAD-BEARING and are used VERBATIM from that
 * research — do not reword, shorten or "make warmer":
 *   1. the italic "Your rights on a delay" paragraph (16 CFR 435.2(b)–(c); the
 *      right cannot be contracted away, and the final sentence saying so is part
 *      of the paragraph),
 *   2. "Once printing begins…" — it closes the CONTRACTUAL window and in the
 *      same breath preserves the STATUTORY one. Deleting the second half would
 *      make it an unenforceable "all sales final" clause,
 *   3. the seller identity block (Cal. Bus. & Prof. Code § 17538).
 *
 * The ship window is stated here in exactly the words used everywhere else on
 * this surface. The terms must AGREE with the line above the wizard mount, never
 * restate it differently — a second, differently-worded promise is a second
 * promise.
 *
 * The legal name and the postal address are NOT invented here. Both are the
 * founder's own published text: app/content/legal/codex.md §16.10.2 names
 * "The EGT Universe, LLC, c/o Incorp Services, Inc., 9435 Waterstone Boulevard,
 * Suite 140, Cincinnati, OH 45249" and states it "is also the address used where
 * a law requires a postal address". If the founder wants a different address on
 * a consumer checkout, change it in ONE place: TERMS_SELLER below. */
var TERMS_SELLER = {
  name: "The EGT Universe, LLC",
  dba: "an Ohio limited liability company doing business as EGT",
  address: "c/o Incorp Services, Inc., 9435 Waterstone Boulevard, Suite 140, Cincinnati, OH 45249",
  /* The contact route for cancellation and any question about this order. The
   * old value was a mailto: to the legal@ inbox, which is not a live mailbox —
   * a dead cancellation path is a real problem, so this points at the working
   * contact form (/contact-us, app/pages/contact-us.json). A human reads it.
   * The seller's legal name and street address above are unchanged. (F11.) */
  contactUrl: "/contact-us"
};

function termsItem(heading, body) {
  return { n: "li", c: "egt-reserve-terms__item",
    i: [
      { n: "h3", c: "egt-reserve-terms__k", i: heading },
      { n: "p", c: "egt-reserve-terms__v", i: body }
    ] };
}

function renderTerms() {
  return {
    n: "section", c: "egt-section egt-reserve-terms", id: "preorder-terms",
    i: { n: "div", c: "egt__shell",
      i: { n: "div", c: "egt-glass egt-glass--wide egt-reserve-terms__card egt-reveal",
        i: [
          { n: "p", c: "egt__eyebrow", i: "Preorder terms" },
          { n: "h2", c: "egt-reserve-terms__title", i: "What you're agreeing to." },
          { n: "p", c: "egt-reserve-terms__lead",
            i: "Short, and in plain words. These terms apply to your preorder of the Named Edition, and they are on this page — not behind a link — because you should read them before you pay." },
          { n: "ul", c: "egt-reserve-terms__list",
            i: [
              termsItem("You are charged today, in full.",
                "$75 for the book, plus shipping. Not a hold, not a deposit — a real charge. That money funds the print run."),

              /* OH SALES TAX disclosure. Ohio is destination-sourced, so tax
               * applies only to orders delivered within Ohio and is added as a
               * separate line the buyer sees before paying. Estimated from an
               * operator-confirmable table; finalized per Ohio law. Byte-identical
               * to the wizard Terms step's tax paragraph. */
              termsItem("Ohio addresses: we add Ohio sales tax.",
                "If your book ships to an Ohio address, Ohio sales tax is added as a separate line, calculated from your delivery county. You'll see the exact amount before you pay. Orders shipped outside Ohio have no sales tax added."),

              termsItem("This book is not printed yet.",
                [ "It ships by the end of August 2026. If that changes, we will email you before that date, tell you why, and give you a straight choice: wait, or take a full refund." ]),

              termsItem("Cancel any time before printing begins and we refund you in full.",
                [ "Just ", { n: "a", c: "egt-reserve-terms__link", href: TERMS_SELLER.contactUrl, i: "send us a message" },
                  " and we'll cancel it. No reason needed, no fee, no store credit — the money goes back to the card you paid with, within seven working days." ]),

              /* VERBATIM (research §3). The second sentence is the whole point of
               * the clause. Do not delete it. */
              termsItem("Once printing begins, your book is being made for you",
                "and we can no longer cancel the order. Your legal right to cancel for a delay, described below, still applies."),

              termsItem("If your book arrives damaged, arrives defective, or never arrives, we replace it.",
                "Free. Tell us within 30 days of delivery — or, if it never shows up, whenever you notice. Send a photo if it's damaged so we can go after the printer. We'd rather replace one book than argue about it."),

              /* VERBATIM (research §3), and rendered in italics as drafted. This
               * is the statutory right under 16 CFR 435.2(b)–(c). It cannot be
               * contracted away, and it must not contradict the line above the
               * wizard mount — it does not: both say we notify BEFORE the date
               * passes and you may wait or cancel for a full refund. */
              { n: "li", c: "egt-reserve-terms__item egt-reserve-terms__item--statute",
                i: [
                  { n: "h3", c: "egt-reserve-terms__k", i: "Your rights on a delay, which we cannot take away from you:" },
                  { n: "p", c: "egt-reserve-terms__v egt-reserve-terms__v--statute",
                    i: { n: "em", i: "Federal law gives you the right to cancel this order and receive a full refund if we do not ship within the time stated above, or within 30 days if no time is stated. If we cannot ship on time, we will notify you before that deadline and offer you the choice to wait or to cancel for a prompt refund. Nothing in these terms limits or waives that right." } }
                ] },

              termsItem("We ship to United States addresses only.",
                "The ebook arrives worldwide on the same day the first edition ships."),

              termsItem("What else you get:",
                "a certificate of authenticity carrying a unique identifier, printed and mailed by the studio, and your name recorded in The Named.")
            ] },
          /* VERBATIM in substance (research §3): legal name, complete street
           * address and the refund policy, disclosed BEFORE payment is accepted
           * — Cal. Bus. & Prof. Code § 17538. Must stay on the page. */
          { n: "div", c: "egt-reserve-terms__seller",
            i: [
              { n: "h3", c: "egt-reserve-terms__k", i: "Who you're buying from" },
              { n: "p", c: "egt-reserve-terms__v",
                i: [ { n: "strong", i: TERMS_SELLER.name }, ", " + TERMS_SELLER.dba + ". " + TERMS_SELLER.address + ". ",
                  { n: "a", c: "egt-reserve-terms__link", href: TERMS_SELLER.contactUrl, i: "Contact us" } ] },
              { n: "p", c: "egt-reserve-terms__v",
                i: [ "Our full terms of service and privacy policy are ",
                  { n: "a", c: "egt-reserve-terms__link", href: "/legal/codex", i: "The EGT Codex" },
                  " — you review and agree to it inside the preorder flow, before you pay." ] }
            ] }
        ]
      } }
  };
}
